import {
    PaymentPlanParameters,
    ReservationExtent,
    ServiceOrderStates,
    useRepositoryMutation,
    useRepositoryQuery,
    useRepositorySuspenseQuery,
    useInvalidateRepositoryQuery,
    PaymentMethod,
    PaymentPlansGetTableRecordsRequestParameters,
} from '@mews-commander/data-access';
import { WithAccountId, WithCustomerId, WithEnterpriseId } from '@mews-commander/types';
import { lt, showSuccessAlert } from '@mews/framework';
import { useInvalidateGetAllPaymentRequests } from '../PaymentRequest/hooks';
import { useInvalidatePaymentMethodsTableRecordsQuery } from '../../../queries';
import { useInvalidatePaymentMethodDetails } from '../../sidePanels/PaymentMethodDetailSidebar/queries';
import { WithPaymentPlanId } from './types';

const RESERVATION_EXTENT = {
    orderItems: true,
    products: true,
    reservationGroups: false,
    availabilityBlocks: false,
    companionships: false,
    accounts: false,
    businessSegments: false,
    generalNotes: false,
    channelManagerNotes: false,
    specialRequestNotes: false,
    vouchers: false,
    partnerCompanies: false,
    rates: false,
    travelAgencies: false,
    origin: false,
    bookingEngines: false,
    purchaseOrderNumbers: false,
} satisfies ReservationExtent;

const SERVICE_ORDER_STATES = [ServiceOrderStates.Confirmed, ServiceOrderStates.Started];

export const useGetPaymentPlans = ({ customerId }: WithCustomerId) =>
    useRepositoryQuery('paymentPlans', 'getAll', { customerId });

export const useGetPaymentPlansSuspense = ({ customerId }: WithCustomerId) =>
    useRepositorySuspenseQuery('paymentPlans', 'getAll', { customerId });

export const useGetPaymentPlanDetail = ({ id }: { id: PaymentPlanParameters['id'] }) =>
    useRepositoryQuery('paymentPlans', 'getById', { id });

export const useInvalidateGetPaymentPlans = () => {
    const invalidateRepositoryQuery = useInvalidateRepositoryQuery();
    return () => {
        void invalidateRepositoryQuery('paymentPlans', 'getAll');
    };
};

export const useInvalidateGetPaymentPlansById = () => {
    const invalidateRepositoryQuery = useInvalidateRepositoryQuery();
    return (id: PaymentPlanParameters['id'] | undefined | null) => {
        if (!id) return;
        void invalidateRepositoryQuery('paymentPlans', 'getById', { id });
    };
};

export const useInvalidateGetPaymentPlansTableRecords = () => {
    const invalidateRepositoryQuery = useInvalidateRepositoryQuery();
    return (id: PaymentPlansGetTableRecordsRequestParameters['id'] | undefined | null) => {
        if (!id) return;
        void invalidateRepositoryQuery('paymentPlans', 'getTableRecords', { id });
    };
};

export const useGetServicesSuspense = ({ enterpriseId }: WithEnterpriseId) =>
    useRepositorySuspenseQuery('services', 'getAll', { enterpriseIds: [enterpriseId] });

export const useGetReservationsSuspense = ({ accountId }: WithAccountId) =>
    useRepositorySuspenseQuery('reservations', 'getAll', {
        accountIds: [accountId],
        states: SERVICE_ORDER_STATES,
        extent: RESERVATION_EXTENT,
    });

export const useCreatePaymentPlan = ({ accountId }: WithAccountId) => {
    const invalidateGetAllPaymentRequests = useInvalidateGetAllPaymentRequests();
    return useRepositoryMutation('paymentPlans', 'create', {
        invalidateQueries: {
            paymentPlans: ['getAll'],
        },
        onSuccess: () => {
            invalidateGetAllPaymentRequests({ accountId });
            showSuccessAlert({
                title: lt('CreatePaymentPlanSuccessMessage'),
            });
        },
    });
};

export const useCancelPaymentPlan = ({
    accountId,
    paymentMethodId,
    paymentMethod,
}: WithAccountId & { paymentMethodId?: string; paymentMethod?: PaymentMethod }) => {
    const invalidateGetPaymentPlans = useInvalidateGetPaymentPlans();
    const invalidateGetPaymentPlansById = useInvalidateGetPaymentPlansById();
    const invalidateGetPaymentPlansTableRecords = useInvalidateGetPaymentPlansTableRecords();
    const invalidateGetAllPaymentRequests = useInvalidateGetAllPaymentRequests();
    const invalidatePaymentMethods = useInvalidatePaymentMethodsTableRecordsQuery();
    const invalidatePaymentMethodDetails = useInvalidatePaymentMethodDetails();

    return useRepositoryMutation('paymentPlans', 'cancel', {
        onSuccess: (_, { id }) => {
            invalidateGetPaymentPlans();
            invalidateGetPaymentPlansById(id);
            invalidateGetPaymentPlansTableRecords(id);
            invalidateGetAllPaymentRequests({ accountId });
            invalidatePaymentMethods({ customerId: accountId });
            if (paymentMethodId && paymentMethod) {
                invalidatePaymentMethodDetails({
                    id: paymentMethodId,
                    accountId,
                    paymentMethod,
                });
            }

            showSuccessAlert({
                title: lt('CancelPaymentPlanSuccessMessage'),
            });
        },
    });
};

export const useCancelFuturePayment = ({ paymentPlanId }: WithPaymentPlanId) => {
    const invalidateGetPaymentPlansTableRecords = useInvalidateGetPaymentPlansTableRecords();
    const invalidateGetPaymentPlansById = useInvalidateGetPaymentPlansById();

    return useRepositoryMutation('paymentPlans', 'cancelFuturePayment', {
        onSuccess: () => {
            invalidateGetPaymentPlansById(paymentPlanId);
            invalidateGetPaymentPlansTableRecords(paymentPlanId);
            showSuccessAlert({
                title: lt('CancelFuturePaymentSuccessMessage'),
            });
        },
    });
};
